import React from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Col, Row } from '@amzn/stencil-react-components/layout'
import { Modal } from '@amzn/stencil-react-components/modal'
import { Text } from '@amzn/stencil-react-components/text'

import { ModalContent } from 'src/components/ModalContent'
import { ContainerImportPlan } from 'src/models/dto/item-pools/ItemPoolMetadataDTO'
import { ModuleStatus } from 'src/models/dto/ModuleStatus'
import {
    ContainerImportAction,
    containerImportAction,
    PlannedContainer,
    pluralize,
} from 'src/pages/import-content/ImportContentModel'

export interface ImportConfirmationModalProps {
    isOpen: boolean
    containers: PlannedContainer[]
    onCancel: () => void
    onConfirm: () => void
}

const describeAction = (plan?: ContainerImportPlan): string => {
    switch (containerImportAction(plan)) {
        case ContainerImportAction.Create:
            return 'Creates a new module'
        case ContainerImportAction.Update:
            return 'Updates the existing draft'
        case ContainerImportAction.NewVersion:
            return `Creates a new draft version (the current version is ${
                plan?.existingModuleStatus ? ModuleStatus[plan.existingModuleStatus] : 'not a draft'
            })`
    }
}

// Before an import that creates Item Container modules, list which modules it creates and which
// existing ones it updates, and ask the author to confirm.
export const ImportConfirmationModal = ({
    isOpen,
    containers,
    onCancel,
    onConfirm,
}: ImportConfirmationModalProps) => {
    const createCount = containers.filter(
        ({ plan }) => containerImportAction(plan) === ContainerImportAction.Create
    ).length
    const updateCount = containers.length - createCount

    return (
        <Modal isOpen={isOpen} close={onCancel}>
            <ModalContent
                titleText='Confirm import'
                maxWidth='60vw'
                buttons={[
                    <Button
                        key='cancel'
                        dataTestId='import-confirmation-cancel'
                        variant={ButtonVariant.Secondary}
                        onClick={onCancel}
                    >
                        Cancel
                    </Button>,
                    <Button
                        key='confirm'
                        dataTestId='import-confirmation-confirm'
                        variant={ButtonVariant.Primary}
                        onClick={onConfirm}
                    >
                        Import
                    </Button>,
                ]}
            >
                <Col gridGap='S300' width='50vw' dataTestId='import-confirmation'>
                    <Text dataTestId='import-confirmation-summary'>
                        This import creates {pluralize(createCount, 'Item Container module')} and
                        updates {pluralize(updateCount, 'existing module')}.
                    </Text>
                    {containers.map(({ name, rowCount, plan }) => (
                        <Row
                            key={name}
                            gridGap='S300'
                            justifyContent='space-between'
                            dataTestId={`import-confirmation-${name}`}
                        >
                            <Col gridGap='S100'>
                                <Text fontSize='T200' fontWeight='bold'>
                                    {name}
                                </Text>
                                <Text fontSize='T100' color='neutral70'>
                                    {pluralize(rowCount, 'row')}
                                </Text>
                            </Col>
                            <Text fontSize='T100'>{describeAction(plan)}</Text>
                        </Row>
                    ))}
                </Col>
            </ModalContent>
        </Modal>
    )
}
